<script setup lang="ts">
import { computed, useId } from 'vue';
import { cn } from '../../design-system/utils';
import type { Size } from '../../design-system/tokens';

interface Props {
  modelValue?: string | number;
  type?: string;
  label?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  size?: Size;
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  size: 'md',
  required: false,
  disabled: false,
});

defineEmits<{ 'update:modelValue': [value: string] }>();

const generatedId = useId();
const inputId = computed(() => props.id || `input-${generatedId}`);
const hintId = computed(() => `${inputId.value}-hint`);
const errorId = computed(() => `${inputId.value}-error`);

const sizeClasses = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-3 text-sm',
  lg: 'h-12 px-4 text-base',
};

const inputClasses = computed(() =>
  cn(
    'w-full rounded-md border bg-card text-text placeholder:text-text-muted',
    'transition-colors duration-fast',
    'focus-visible:outline-none focus-visible:shadow-focus focus-visible:border-accent',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-muted',
    sizeClasses[props.size],
    props.error ? 'border-danger' : 'border-border hover:border-border-strong',
  ),
);
</script>

<template>
  <div class="flex flex-col gap-1">
    <label
      v-if="label"
      :for="inputId"
      class="text-sm font-medium text-text-secondary"
    >
      {{ label }}
      <span v-if="required" class="text-danger" aria-hidden="true">*</span>
    </label>
    <input
      :id="inputId"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :aria-invalid="!!error"
      :aria-describedby="cn(hint && hintId, error && errorId) || undefined"
      :class="inputClasses"
      @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
    <p v-if="hint && !error" :id="hintId" class="text-xs text-text-muted">
      {{ hint }}
    </p>
    <p v-if="error" :id="errorId" class="text-xs text-danger" role="alert">
      {{ error }}
    </p>
  </div>
</template>
